import { getCredentialsFromCacheOrCognito } from "@amzn/midway-cognito-type-script-provider";
import type { AwsCredentialIdentityProvider } from "@aws-sdk/types";
import { AwsRum, type AwsRumConfig } from "aws-rum-web";

import { authStore } from "../store/auth";

interface ErrorEvent {
    message?: string;
}

// Define errors that should not trigger alarms in RUM
const ignoredErrorMessages = new Set<string>([
    "GM_configStruct is not defined",
]);

export async function initiateRUMClient(): Promise<AwsRum | undefined> {
    const { settings } = authStore.getState();
    const {
        rumAppId,
        rumRegion,
        apiEndpoint,
        ppApiEndpoint,
        discoveryApiEndpoint,
        adsApiEndpoint,
        g4sApiEndpoint,
        plmApiEndpoint,
        blinkApiEndpoint,
        g4sServiceEndpoint,
        seleneApiEndpoint,
        magnetoApiEndpoint,
        aquilaApiEndpoint,
        forgeApiEndpoint,
    } = settings;

    if (!rumAppId || !rumRegion) {
        return undefined;
    }

    try {
        // Create a credentials provider that fetches fresh credentials on each call
        const credentialsProvider: AwsCredentialIdentityProvider = async () => {
            try {
                const creds = await getCredentialsFromCacheOrCognito({
                    region: rumRegion,
                    identityPoolId: settings.cognitoPoolId,
                    clientId: window.location.host,
                    redirectUri: window.location.href,
                });

                if (!creds.accessKeyId || !creds.secretAccessKey) {
                    console.error("[RUM] Invalid credentials received");
                    throw new Error("Invalid credentials");
                }

                return {
                    accessKeyId: creds.accessKeyId,
                    secretAccessKey: creds.secretAccessKey,
                    sessionToken: creds.sessionToken,
                    expiration: creds.expiration,
                };
            } catch (error) {
                console.error("[RUM] Credential provider failed:", error);
                throw error;
            }
        };

        // Configure RUM client with simplified RegExp patterns to avoid potential issues
        const config: AwsRumConfig = {
            allowCookies: true,
            endpoint: `https://dataplane.rum.${rumRegion}.amazonaws.com`,
            sessionSampleRate: 1,
            telemetries: [
                [
                    "errors",
                    {
                        ignore: (errorEvent: ErrorEvent) => {
                            return (
                                typeof errorEvent.message === "string" &&
                                ignoredErrorMessages.has(errorEvent.message)
                            );
                        },
                    },
                ],
                "performance",
                [
                    "http",
                    {
                        recordAllRequests: true,
                        urlsToInclude: [
                            new RegExp(`.*${apiEndpoint}.*`),
                            new RegExp(`.*${ppApiEndpoint}.*`),
                            new RegExp(`.*${discoveryApiEndpoint}.*`),
                            new RegExp(
                                `.*${aquilaApiEndpoint.getEndpoint()}.*`,
                            ),
                            new RegExp(`.*${forgeApiEndpoint.getEndpoint()}.*`),
                            new RegExp(`.*${adsApiEndpoint}.*`),
                            new RegExp(`.*${g4sApiEndpoint}.*`),
                            new RegExp(`.*${blinkApiEndpoint}.*`),
                            new RegExp(`.*${g4sServiceEndpoint}.*`),
                            new RegExp(`.*${seleneApiEndpoint}.*`),
                            new RegExp(`.*${magnetoApiEndpoint}.*`),
                            ...(plmApiEndpoint
                                ? [new RegExp(`.*${plmApiEndpoint}.*`)]
                                : []),
                        ],
                    },
                ],
            ],
            pagesToInclude: [
                new RegExp(`https://${window.location.host}/#/*`),
                new RegExp(`https://${window.location.host}/`),
            ],
            pageIdFormat: "PATH_AND_HASH",
            enableXRay: true,
        };

        const awsRum: AwsRum = new AwsRum(rumAppId, "1.0.0", rumRegion, config);
        awsRum.setAwsCredentials(credentialsProvider);
        return awsRum;
    } catch (error) {
        console.error("[RUM] Error while loading RUM client:", error);
        return undefined;
    }
}
